/**
 * Company settings — the working day, leave entitlement and holiday calendar.
 * Route: /settings
 * @file src/web/features/settings/pages/index.tsx
 *
 * @llm-rule WHEN: A Company Admin changes how the company itself works
 * @llm-rule NOTE: These four groups had a working API and no UI at all, so the
 *   working day, weekly offs, country and leave entitlement could only be
 *   changed with a curl. This is that surface.
 * @llm-rule AVOID: Showing this to anyone but a Company Admin — the server
 *   rejects everyone else, and offering a form that always fails is worse than
 *   offering nothing.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Input, Skeleton, toast } from '@bloomneo/uikit';
import { CalendarDays, Clock, Globe2, Image as ImageIcon, Plane } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel, SectionLabel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import type { OrgRole } from '../../../shared/domain';

interface Settings {
  workStartTime: string;
  workEndTime: string;
  weeklyOffDays: number[];
  timezone: string;
  country: string;
  casualLeaveDays: number;
  sickLeaveDays: number;
  logo: string | null;
}

interface Country {
  code: string;
  name: string;
  timezone: string;
}

/** JS weekday numbers, Monday first — the order a working week is read in. */
const WEEKDAYS: Array<{ n: number; label: string }> = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
  { n: 6, label: 'Sat' },
  { n: 0, label: 'Sun' },
];

function SettingsPage() {
  const { user } = useAuth();
  const { request } = useAppRequest();

  const [settings, setSettings] = useState<Settings | null>(null);
  const [saved, setSaved] = useState<Settings | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const result = await request<{ settings: Settings; countries: Country[] }>('/api/org/settings');
    if (result.ok) {
      setSettings(result.data.settings);
      setSaved(result.data.settings);
      setCountries(result.data.countries ?? []);
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = JSON.stringify(settings) !== JSON.stringify(saved);

  function patch(next: Partial<Settings>) {
    setSettings((prev) => (prev ? { ...prev, ...next } : prev));
  }

  function toggleOff(day: number) {
    if (!settings) return;
    const has = settings.weeklyOffDays.includes(day);
    patch({
      weeklyOffDays: has
        ? settings.weeklyOffDays.filter((d) => d !== day)
        : [...settings.weeklyOffDays, day].sort(),
    });
  }

  async function save() {
    if (!settings) return;
    if (settings.workEndTime <= settings.workStartTime) {
      return toast.error('The end of the day must be after the start');
    }

    setBusy(true);
    try {
      const result = await request<{ message: string; settings: Settings }>('/api/org/settings', {
        method: 'PUT',
        body: settings,
      });
      if (!result.ok) return toast.error(result.error);
      setSaved(result.data.settings);
      setSettings(result.data.settings);
      toast.success(result.data.message);
    } finally {
      setBusy(false);
    }
  }

  // The server refuses everyone else, so do not render a form that cannot save.
  const role = (user?.orgRole as OrgRole) ?? 'EMPLOYEE';
  if (role !== 'COMPANY_ADMIN') return <Navigate to="/clock" replace />;

  return (
    <AppShell
      title="Company settings"
      subtitle="The working day, time off, and which country's holidays apply."
    >
      {loading || !settings ? (
        <Skeleton className="h-96 w-full rounded-[18px]" />
      ) : (
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          {/* ------------------------------- Logo ------------------------------- */}
          <Panel>
            <div className="flex items-center gap-2">
              <ImageIcon className="size-4 text-ink-faint" strokeWidth={1.8} />
              <SectionLabel>Company logo</SectionLabel>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="grid size-14 flex-none place-items-center overflow-hidden rounded-[12px] border border-line bg-paper-sunk">
                {settings.logo ? (
                  <img src={settings.logo} alt="Company logo" className="size-full object-contain" />
                ) : (
                  <ImageIcon className="size-5 text-ink-ghost" strokeWidth={1.6} />
                )}
              </span>

              <div className="flex flex-col gap-1.5">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    // 500 KB, matching the server. Checked here too so someone
                    // picking a 4 MB photo is told immediately rather than after
                    // a round trip.
                    if (file.size > 512_000) {
                      toast.error('That image is larger than 500 KB');
                      e.target.value = '';
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => patch({ logo: String(reader.result) });
                    reader.onerror = () => toast.error('That file could not be read');
                    reader.readAsDataURL(file);
                  }}
                  className="text-[12.5px] text-ink-soft file:mr-3 file:rounded-[8px] file:border file:border-line file:bg-paper file:px-3 file:py-1.5 file:text-[12.5px] file:text-ink"
                />
                {settings.logo && (
                  <button
                    type="button"
                    onClick={() => patch({ logo: null })}
                    className="self-start text-[11.5px] text-ink-ghost underline-offset-2 hover:underline"
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>

            <p className="mt-2 text-[11.5px] leading-relaxed text-ink-soft">
              Replaces the Presynce mark in the header for everyone in your company. PNG, JPEG,
              WebP or SVG, up to 500 KB. A square image works best.
            </p>
          </Panel>

          {/* ---------------------------- Working day --------------------------- */}
          <Panel>
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-ink-faint" strokeWidth={1.8} />
              <SectionLabel>Working day</SectionLabel>
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">Starts</span>
                <Input
                  type="time"
                  value={settings.workStartTime}
                  onChange={(e) => patch({ workStartTime: e.target.value })}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">Ends</span>
                <Input
                  type="time"
                  value={settings.workEndTime}
                  onChange={(e) => patch({ workEndTime: e.target.value })}
                />
              </label>
            </div>

            <p className="mt-2 text-[11.5px] leading-relaxed text-ink-soft">
              A clock-in after the start time is flagged automatically. Someone with their own
              shift keeps it — this is the company default.
            </p>
          </Panel>

          {/* ---------------------------- Weekly offs --------------------------- */}
          <Panel>
            <div className="flex items-center gap-2">
              <CalendarDays className="size-4 text-ink-faint" strokeWidth={1.8} />
              <SectionLabel>Weekly offs</SectionLabel>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {WEEKDAYS.map((d) => {
                const off = settings.weeklyOffDays.includes(d.n);
                return (
                  <button
                    key={d.n}
                    type="button"
                    onClick={() => toggleOff(d.n)}
                    aria-pressed={off}
                    className={`rounded-[10px] border px-3 py-2 text-[12.5px] font-medium transition-colors ${
                      off
                        ? 'border-ink bg-ink text-paper'
                        : 'border-line bg-paper text-ink-soft hover:bg-paper-warm'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>

            <p className="mt-2 text-[11.5px] text-ink-soft">
              Nobody is expected to clock in on these, and they are never counted against anyone.
            </p>
          </Panel>

          {/* -------------------------- Leave entitlement ------------------------ */}
          <Panel>
            <div className="flex items-center gap-2">
              <Plane className="size-4 text-ink-faint" strokeWidth={1.8} />
              <SectionLabel>Leave a year</SectionLabel>
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">Casual leave (days)</span>
                <Input
                  type="number"
                  min="0"
                  max="365"
                  value={settings.casualLeaveDays}
                  onChange={(e) => patch({ casualLeaveDays: Number(e.target.value) })}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">Sick leave (days)</span>
                <Input
                  type="number"
                  min="0"
                  max="365"
                  value={settings.sickLeaveDays}
                  onChange={(e) => patch({ sickLeaveDays: Number(e.target.value) })}
                />
              </label>
            </div>

            <p className="mt-2 text-[11.5px] leading-relaxed text-ink-soft">
              Counted per calendar year. Unpaid leave has no quota — that is what makes it unpaid.
              Changing these affects what is left for the current year, not leave already approved.
            </p>
          </Panel>

          {/* ------------------------------ Country ----------------------------- */}
          <Panel>
            <div className="flex items-center gap-2">
              <Globe2 className="size-4 text-ink-faint" strokeWidth={1.8} />
              <SectionLabel>Holiday calendar</SectionLabel>
            </div>

            <label className="mt-3 flex flex-col gap-1.5">
              <span className="text-[12px] font-medium text-ink-soft">Country</span>
              <select
                className="h-9 rounded-[10px] border border-line bg-paper px-2.5 text-[13.5px] text-ink"
                value={settings.country}
                onChange={(e) => patch({ country: e.target.value })}
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>

            <p className="mt-2 text-[11.5px] leading-relaxed text-ink-soft">
              Changing this replaces the built-in public holidays. Anything you added by hand is
              kept. Lunar festival dates shift yearly — check them against your own observed list.
            </p>
          </Panel>

          <div className="sticky bottom-3 flex justify-end">
            <Button onClick={() => void save()} disabled={!dirty || busy}>
              {busy ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default function CompanySettings() {
  return (
    <AuthGuard>
      <SettingsPage />
    </AuthGuard>
  );
}
